import { useEffect, useMemo, useRef, useState } from "react";
import { boardsToRefresh, renderBulletinBoardImage } from "../lib/bulletinBoardImage";
import { toDateKey } from "../lib/plannerItems";
import { uploadBulletinSnapshot, deleteRoomFile } from "../lib/roomFileStorage";
import { getUnityBridgeCommands } from "../lib/unityCommandBridge";
import { isUnityWorldBuilt } from "../lib/unityEvents";

const SETTLE_MS = 800;
const OLD_IMAGE_GRACE_MS = 10000;
const WORLD_READY_POLL_MS = 1000;
const DAY_CHECK_MS = 60000;

/**
 * Keeps the 3D bulletin board of the room the user is in showing the calendar modal:
 * draws it, shows it in Unity right away (after WorldReady), then uploads it and saves
 * its path so the next world load sends it as a signed URL.
 */
export default function useBulletinBoardImages({ userId, enabled, roomId, districts, surfaceItems, setBulletinImage }) {
  const [tick, setTick] = useState(0);
  const [todayKey, setTodayKey] = useState(() => toDateKey());
  const busyRef = useRef(false);
  const failedRef = useRef(new Map());
  const waitingRef = useRef(new Map());
  const setImageRef = useRef(setBulletinImage);
  setImageRef.current = setBulletinImage;

  useEffect(() => {
    const timer = setInterval(() => setTodayKey(toDateKey()), DAY_CHECK_MS);
    return () => clearInterval(timer);
  }, []);

  const jobs = useMemo(
    () => (enabled && userId ? boardsToRefresh(surfaceItems, districts, roomId, todayKey) : []),
    [enabled, userId, surfaceItems, districts, roomId, todayKey]
  );

  useEffect(() => {
    failedRef.current = new Map();
    waitingRef.current = new Map();
  }, [userId]);

  useEffect(() => {
    const send = (room, content) => {
      if (isUnityWorldBuilt()) {
        waitingRef.current.delete(room);
        getUnityBridgeCommands()?.setSurfaceImage?.(room, "bulletin_board", content);
      } else {
        waitingRef.current.set(room, content);
      }
    };

    const refresh = async (job) => {
      const dataUrl = await renderBulletinBoardImage(job.content);
      send(job.roomId, dataUrl);
      const path = await uploadBulletinSnapshot(userId, job.roomId, dataUrl);
      setImageRef.current?.(job.roomId, { path, updatedAt: Date.now(), signature: job.signature });
      if (job.previousPath) {
        setTimeout(() => {
          deleteRoomFile(job.previousPath).catch((err) =>
            console.warn("[bulletin] could not remove the old board image:", err?.message || err));
        }, OLD_IMAGE_GRACE_MS);
      }
    };

    const todo = jobs.filter((job) => failedRef.current.get(job.roomId) !== job.signature);
    if (!todo.length || busyRef.current) return undefined;

    const timer = setTimeout(async () => {
      busyRef.current = true;
      try {
        for (const job of todo) {
          try {
            await refresh(job);
          } catch (err) {
            failedRef.current.set(job.roomId, job.signature);
            console.warn("[bulletin] could not update the board image:", err?.message || err);
          }
        }
      } finally {
        busyRef.current = false;
        setTick((t) => t + 1);
      }
    }, SETTLE_MS);
    return () => clearTimeout(timer);
  }, [jobs, userId, tick]);

  useEffect(() => {
    if (!enabled) return undefined;
    const timer = setInterval(() => {
      if (!waitingRef.current.size || !isUnityWorldBuilt()) return;
      const commands = getUnityBridgeCommands();
      if (!commands?.setSurfaceImage) return;
      for (const [room, content] of waitingRef.current) {
        commands.setSurfaceImage(room, "bulletin_board", content);
      }
      waitingRef.current.clear();
    }, WORLD_READY_POLL_MS);
    return () => clearInterval(timer);
  }, [enabled]);
}
